/**
 * MobileBottomNav.tsx — the web app's bottom tab bar (mobile browser +
 * installed PWA). The bar is always full-width, solid and edge-to-edge.
 * Every web-rendered mobile surface uses one consistent Material navigation
 * structure, including responsive browser mode and installed PWAs on any OS.
 * True native iOS shells own their native tab bar instead.
 *
 * Constraints that look arbitrary but are not:
 * - The surface is theme-aware (`var(--background)` / `var(--border)`), never a
 *   hardcoded white: that was a live dark-mode PWA regression.
 * - The bar's own background fills the home-indicator zone rather than
 *   floating above it, so the floor lives in its padding-bottom. That floor is
 *   `max(--safe-area-bottom, 34px)`: installed PWAs can report
 *   `env(safe-area-inset-bottom)` as 0 while the indicator still occupies the
 *   bottom of the screen.
 * - The content zone and that floor are published as `--qs-mobile-bottom-nav-*`
 *   (theme.css) so AppLayout and feature screens reserve exactly this much
 *   scroll clearance. Change one, change both.
 * - `data-slot="mobile-bottom-nav-shell"` is what the keyboard-open hide rule
 *   in theme.css targets.
 */
import { useMemo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import {
  Home,
  Folder,
  MessageCircle,
  Bell,
  MoreHorizontal,
  Phone,
} from '@/shared/ui/icons';
import { useMessaging } from '../../features/messages/MessagingContext';
import { useNotifications } from '../../features/notifications/NotificationContext';
import { useEmailUnread } from '../../features/email/EmailUnreadContext';
import { computeMoreTabBadgeCount, resolveActiveBottomNavTabId } from './mobileBottomNavTabs';
import { MobileBottomNavActiveIcon } from './MobileBottomNavActiveIcon';
import { visibleBottomTabs, type BottomTabId } from '../../shared-ui/navigation/bottomTabs';
import { useQuantisVoice } from '../../shared-ui/voice/VoiceContext';

// The web bar keeps its icon components (they share path data with the native
// bar through iconPaths) so its active-state treatment is unchanged.
const TAB_ICONS: Record<BottomTabId, React.ElementType> = {
  dashboard: Home,
  projects: Folder,
  messages: MessageCircle,
  calls: Phone,
  notifications: Bell,
  more: MoreHorizontal,
};

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
  action?: () => void;
  show?: boolean;
}

export interface ProjectTabProps {
  isClient: boolean;
  isEstimatingPhase: boolean;
  activeTasksCount: number;
  isStaff: boolean;
  projectId?: string;
  projectName?: string;
  projectStage?: string;
  internalUserIds?: string[];
}

export interface MobileProjectNavContext {
  isProjectDetailView?: boolean;
  projectActiveTab?: string;
  onProjectTabChange?: (tab: string) => void;
  projectTabProps?: ProjectTabProps;
  projectPermissions?: any;
}

interface MobileBottomNavProps {
  user: any;
  currentView: string;
  onNavigate: (view: string) => void;
  onLogout: () => void;
  shellRuntime?: 'ios' | 'macos' | 'android' | 'browser';
  nativePlatform?: 'ios' | 'macos' | 'android' | 'web';
  isNative?: boolean;
}

// ── Bar geometry ─────────────────────────────────────────────────────────────
// The content zone (icons + labels) and the home-indicator floor beneath it are
// published as CSS vars (theme.css) so every screen's scroll clearance tracks
// them. The literals below are fallbacks for subtrees the shell vars don't
// reach; keep them in step with theme.css.
const BAR_CONTENT_HEIGHT = 'var(--qs-mobile-bottom-nav-min-height, 55px)';
const BAR_VERTICAL_PADDING = 'var(--qs-mobile-bottom-nav-vertical-padding, 3px)';
// Home-indicator floor: installed PWAs can report env(safe-area-inset-bottom)
// as 0 while the indicator still owns the bottom of the screen. The bar's
// background fills this zone, so it is padding, not a gap.
const BAR_SAFE_FLOOR =
  'var(--qs-mobile-bottom-nav-safe-floor, max(var(--safe-area-bottom, 0px), 34px))';

// Springs, never linear. The press scale rides variant propagation and the
// badge animates itself, so nothing on the tap path writes React state per
// frame.
const PRESS_SPRING = { type: 'spring', stiffness: 520, damping: 30, mass: 0.6 } as const;
const BADGE_SPRING = { type: 'spring', stiffness: 600, damping: 22, mass: 0.5 } as const;
const PRESS_VARIANTS = { rest: { scale: 1 }, pressed: { scale: 0.94 } };
const ANDROID_PRESS_VARIANTS = { rest: { scale: 1 }, pressed: { scale: 0.98 } };

export function MobileBottomNav({
  currentView,
  onNavigate,
  user,
  onLogout,
  shellRuntime = 'browser',
  nativePlatform = 'web',
  isNative = false,
}: MobileBottomNavProps) {
  const { unreadCount: unreadMessageCount }    = useMessaging();
  const { unreadCount: unreadNotificationCount } = useNotifications();
  const { inboxUnread: unreadEmailCount } = useEmailUnread();
  const reduceMotion = useReducedMotion();

  // The native iOS shell renders its own UITabBar (Liquid Glass on iOS 26), so
  // this bar must not paint there at all — it lives inside the WebView, above
  // the native chrome in the visual stack, and would be the only bar the user
  // sees. The bail-out fires AFTER every hook (see the guard just before the
  // return): `isNative` flips false -> true when the bridge announces itself,
  // and an early return here would change the hook count mid-life.
  const nativeTabBarOwnsNav =
    isNative && (nativePlatform === 'ios' || shellRuntime === 'ios');
  const usesMaterialNavigation = !isNative;

  // The tab set is FIXED — the same five destinations on every screen,
  // including inside a project. It used to swap to project shortcuts
  // (Dashboard / Pricing / Documents / More / Search, and a capture-first
  // variant when the user was physically on site), which meant the bar
  // rearranged itself mid-session and "Projects" stopped being reachable
  // from the one control that should always offer it. A tab bar is the
  // app's fixed floor, not a contextual toolbar: project tabs belong to
  // the project screen's own chrome.
  // More's badge rolls up unread counts from the destinations it hides
  // (Email, today) — otherwise an unread count sitting behind More is
  // invisible until the user happens to open it. Built from the same
  // sidebar-groups call MoreView renders its own list from, so the two
  // can't drift apart the way the pinned-id sets used to (see
  // mobileBottomNavTabs.ts).
  const voiceLine = useQuantisVoice()?.config?.enabled === true;
  const moreBadgeCount = useMemo(
    () => computeMoreTabBadgeCount({ role: user?.role, unreadEmailCount }),
    [user?.role, unreadEmailCount],
  );

  const navItems = useMemo<NavItem[]>(
    () => [
      // Ids and labels come from the shared list the native tab bar also reads.
      // More navigates to the More page (tab-bar More convention) rather than
      // opening a sheet, which lets the large title and scroll-edge header work.
      ...visibleBottomTabs(voiceLine).map((tab) => ({
        id: tab.id,
        label: tab.label,
        icon: TAB_ICONS[tab.id],
        badge: {
          dashboard: 0,
          projects: 0,
          messages: unreadMessageCount,
          calls: 0,
          notifications: unreadNotificationCount,
          more: moreBadgeCount,
        }[tab.id],
      })),
    ],
    [unreadMessageCount, unreadNotificationCount, moreBadgeCount, voiceLine],
  );

  // Selection is resolved by id, not by index: the bar is rendered on every
  // view, so a view outside the pinned five still needs a truthful answer.
  // mobileBottomNavTabs maps those to More (which is literally the index of
  // everything not pinned here); the old index fallback lit Home instead, so
  // Calendar, Sales, Settings and the rest all claimed to be Home.
  // Only currentView changes the result — navItems' ids are fixed and its
  // identity churns with every badge count.
  const activeTabId = useMemo(() => resolveActiveBottomNavTabId(currentView), [currentView]);

  const handleNavigate = (item: NavItem) => {
    // Drop focus from the bottom-nav button before opening any modal/drawer.
    // Vaul/Radix apply `aria-hidden="true"` to siblings of the open drawer —
    // if the trigger button is still focused, that produces an a11y violation
    // ("aria-hidden on an element with focus inside it").
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (item.action) {
      item.action();
    } else {
      onNavigate(item.id);
    }
  };

  // Every hook has run by here — see `nativeTabBarOwnsNav` above for why the
  // native bail-out is not an early return at the top of the component.
  if (nativeTabBarOwnsNav) return null;

  return (
    <nav
      data-slot="mobile-bottom-nav-shell"
      // No "Navigation" in the label: the <nav> element's implicit role
      // already announces "navigation", so including the word here made a
      // screen reader read "Mobile Bottom Navigation, navigation".
      aria-label="Bottom tab bar"
      className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none"
      style={{
        // Lifts the whole bar above mobile-browser chrome (Safari's bottom
        // address bar, measured via the Visual Viewport API and published as
        // --qs-browser-chrome-bottom; always 0 in installed PWAs and native
        // shells). The home-indicator zone is padding INSIDE the bar, not here,
        // so the bar's surface reaches the bottom of the screen.
        paddingBottom: 'var(--qs-browser-chrome-bottom, 0px)',
      }}
    >
      <div
        data-slot={usesMaterialNavigation ? 'android-navigation-bar' : 'ios-tab-bar'}
        data-interface={usesMaterialNavigation ? 'material' : 'default'}
        className="pointer-events-auto"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'stretch',
          // Explicit border-box + overflow-visible so nothing inside can be
          // clipped at the bottom by min-height or an ancestor's
          // overflow: hidden — labels were getting cut off against the
          // home-indicator zone in the dark-mode PWA.
          boxSizing: 'border-box',
          overflow: 'visible',
          // Edge to edge. No max-width, no inset, no radius: the floating
          // capsule was rejected outright, and half-measures (a wide pill, a
          // rounded top) land back in the same place.
          width: '100%',
          // Content zone + the home-indicator floor, which the bar's own
          // background fills rather than floating above.
          minHeight: `calc(${BAR_CONTENT_HEIGHT} + ${BAR_SAFE_FLOOR})`,
          padding: `${BAR_VERTICAL_PADDING} ${usesMaterialNavigation ? '4px' : '8px'} calc(${BAR_VERTICAL_PADDING} + ${BAR_SAFE_FLOOR})`,
          // SOLID, not glass. Translucency + blur was tried and rejected; the
          // token keeps it theme-aware (solid white in light, solid dark
          // surface in dark), which is the part that must never regress to a
          // hardcoded colour. No backdrop-filter: nothing shows through.
          // Keep the navigation surface neutral like the top app bar. Purple
          // belongs to selection, not to both the surface and its indicator.
          background: 'var(--background)',
          borderTop: usesMaterialNavigation ? 'none' : '1px solid var(--border)',
          // Material mobile chrome sits one layer above the scrolling page.
          // Two soft upward shadows create depth without drawing a hard line.
          boxShadow: usesMaterialNavigation
            ? '0 -2px 5px color-mix(in srgb, var(--foreground) 9%, transparent), 0 -10px 24px -14px color-mix(in srgb, var(--foreground) 28%, transparent)'
            : 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
            gap: usesMaterialNavigation ? 0 : '2px',
            position: 'relative',
          }}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === activeTabId;
            const badgeCount = item.badge ?? 0;
            const hasBadge = badgeCount > 0;
            const badgeText = badgeCount > 99 ? '99+' : String(badgeCount);

            return (
              <motion.button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item)}
                data-active={isActive}
                // A variant label on whileTap makes this button a variant
                // parent, so the press scale is broadcast to the icon+label
                // group below rather than applied to the whole cell.
                // `animate="rest"` gives the release a definite target rather
                // than an implicit revert.
                initial={false}
                animate="rest"
                whileTap="pressed"
                aria-current={isActive ? 'page' : undefined}
                // The visible label already names the tab; the badge count is
                // the part a screen reader would otherwise miss.
                aria-label={hasBadge ? `${item.label}, ${badgeText} unread` : undefined}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // 48px tall x ~72px wide: comfortably past the 44pt floor.
                  minHeight: usesMaterialNavigation ? '64px' : '48px',
                  minWidth: '48px',
                  flex: '1 0 0',
                  padding: '0 2px',
                  position: 'relative',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  borderRadius: usesMaterialNavigation ? 0 : '14px',
                  color: isActive
                    ? 'var(--primary)'
                    : usesMaterialNavigation
                      ? 'color-mix(in srgb, var(--foreground) 68%, transparent)'
                      : 'var(--label-secondary)',
                  transition: 'color var(--transition-fast)',
                }}
              >
                {/* Selection is carried by the glyph and label, without an overlay. */}
                <motion.span
                  variants={usesMaterialNavigation ? ANDROID_PRESS_VARIANTS : PRESS_VARIANTS}
                  transition={reduceMotion
                    ? { duration: 0 }
                    : usesMaterialNavigation
                      ? { duration: 0.12, ease: 'easeOut' }
                      : PRESS_SPRING}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: usesMaterialNavigation ? '4px' : '2px',
                    pointerEvents: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: usesMaterialNavigation ? '32px' : '24px',
                      height: usesMaterialNavigation ? '32px' : '24px',
                      borderRadius: 0,
                      background: 'transparent',
                    }}
                  >
                    {usesMaterialNavigation && isActive && item.id !== 'more' ? (
                      <MobileBottomNavActiveIcon
                        tabId={item.id}
                        style={{ width: '24px', height: '24px', color: 'currentColor' }}
                      />
                    ) : (
                      <Icon
                        style={{
                          width: usesMaterialNavigation ? '24px' : '22px',
                          height: usesMaterialNavigation ? '24px' : '22px',
                          color: 'currentColor',
                          strokeWidth: isActive ? 2.15 : 1.85,
                          transition: 'stroke-width var(--transition-fast)',
                        }}
                      />
                    )}

                    {hasBadge && (
                      <motion.span
                        initial={reduceMotion ? false : { scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={reduceMotion ? { duration: 0 } : BADGE_SPRING}
                        style={{
                          position: 'absolute',
                          top: usesMaterialNavigation ? '-3px' : '-5px',
                          right: usesMaterialNavigation ? '-6px' : '-9px',
                          minWidth: usesMaterialNavigation ? '15px' : '16px',
                          height: usesMaterialNavigation ? '15px' : '16px',
                          padding: usesMaterialNavigation ? '0 3px' : '0 4px',
                          fontFamily: 'var(--font-apple)',
                          fontSize: '10px',
                          fontWeight: 'var(--font-weight-bold)',
                          lineHeight: 1,
                          letterSpacing: '0.1px',
                          backgroundColor: 'var(--status-danger)',
                          color: 'white',
                          borderRadius: '9999px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          // A ring in the bar's own surface colour rather than a
                          // drop shadow: it cuts the badge cleanly away from
                          // the glyph underneath it, and a shadow only reads on
                          // a light backdrop.
                          boxShadow: '0 0 0 2px var(--background)',
                          zIndex: 2,
                        }}
                      >
                        {badgeText}
                      </motion.span>
                    )}
                  </span>

                  <span
                    style={{
                      // `display: block` is explicit so the label always
                      // renders as a flex item (default inline spans were
                      // collapsing to zero visible height on some PWA
                      // webviews), and the 14px line box keeps SF Pro
                      // descenders (g/j/p/q/y — "Projects") inside the box
                      // rather than pushing them into an ancestor's clip.
                      display: 'block',
                      fontFamily: usesMaterialNavigation ? 'var(--font-sans)' : 'var(--font-apple)',
                      fontSize: usesMaterialNavigation ? '12px' : '10px',
                      fontWeight: isActive
                        ? 'var(--font-weight-semibold)'
                        : 'var(--font-weight-medium)',
                      lineHeight: usesMaterialNavigation ? '16px' : '14px',
                      letterSpacing: '0.1px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      color: 'currentColor',
                    }}
                  >
                    {item.label}
                  </span>
                </motion.span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
